import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, AlertTriangle, XCircle, RefreshCw, ArrowLeft, Zap, Database, Wifi, Globe } from 'lucide-react';
import useSeo from '@/hooks/useSeo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GOOD = '#22C55E';
const WARN = '#F59E0B';
const BAD = '#EF4444';

function Row({ icon: Icon, label, status, detail }) {
  const color = status === 'ok' ? GOOD : status === 'warn' ? WARN : BAD;
  const StatusIcon = status === 'ok' ? CheckCircle2 : status === 'warn' ? AlertTriangle : XCircle;
  return (
    <div className="flex items-start gap-4 p-4 rounded-2xl bg-white/[0.03] border border-white/10" data-testid={`health-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${color}20`, border: `1px solid ${color}50` }}>
        <Icon size={18} style={{ color }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-white font-semibold text-sm">{label}</span>
          <StatusIcon size={14} style={{ color }} />
        </div>
        <div className="text-xs text-[#9CA3AF] break-words">{detail}</div>
      </div>
    </div>
  );
}

export default function HealthPage() {
  useSeo({
    title: 'Service Status · EntrepreNatives',
    description: 'Check whether EntrepreNatives is running smoothly. Live diagnostics for our servers, database, and connectivity.',
    url: 'https://entreprenatives.com/health',
    noindex: true,
  });

  const [results, setResults] = useState(null);
  const [checking, setChecking] = useState(false);
  const [startedAt, setStartedAt] = useState(null);

  const runChecks = async () => {
    setChecking(true);
    setStartedAt(new Date());
    const out = {
      internet: { status: 'checking', detail: 'Checking browser connectivity…' },
      dns:       { status: 'checking', detail: 'Resolving entreprenatives.com…' },
      backend:   { status: 'checking', detail: 'Pinging API…' },
      database:  { status: 'checking', detail: 'Testing database…' },
      origin:    { status: 'checking', detail: 'Verifying same-origin routing…' },
    };
    setResults({ ...out });

    // 1. Internet reachability (Cloudflare's fast trace endpoint)
    try {
      const r = await fetch('https://1.1.1.1/cdn-cgi/trace', { cache: 'no-store' });
      out.internet = r.ok
        ? { status: 'ok', detail: 'Your device can reach the internet.' }
        : { status: 'warn', detail: `Edge responded with ${r.status}.` };
    } catch (e) {
      out.internet = { status: 'bad', detail: `Browser cannot reach external endpoints: ${e.message || 'unknown error'}` };
    }
    setResults({ ...out });

    // 2. DNS / TLS resolution for the site itself
    try {
      const t0 = performance.now();
      const r = await fetch('/favicon.ico', { cache: 'no-store' });
      const took = Math.round(performance.now() - t0);
      out.dns = r.ok
        ? { status: 'ok', detail: `Resolved in ${took} ms.` }
        : { status: 'warn', detail: `DNS OK but site returned ${r.status}.` };
    } catch (e) {
      out.dns = { status: 'bad', detail: `Cannot resolve this site: ${e.message || 'unknown error'}` };
    }
    setResults({ ...out });

    // 3. Backend /api/health
    try {
      const t0 = performance.now();
      const r = await fetch(`${API}/health`, { cache: 'no-store' });
      const took = Math.round(performance.now() - t0);
      if (r.ok) {
        const data = await r.json();
        const slow = took > 1500;
        out.backend = {
          status: slow ? 'warn' : 'ok',
          detail: `API reachable in ${took} ms${slow ? ' (slow — expected < 1.5 s)' : ''}.`,
        };
        out.database = data.db === 'ok'
          ? { status: 'ok', detail: `Database healthy · server latency ${data.response_ms} ms.` }
          : { status: 'bad', detail: 'Database ping failed — admins have been notified.' };
      } else {
        out.backend = { status: 'bad', detail: `API returned HTTP ${r.status}.` };
        out.database = { status: 'warn', detail: 'Could not verify database (API down).' };
      }
    } catch (e) {
      out.backend = { status: 'bad', detail: `Failed to reach API: ${e.message || 'unknown error'}` };
      out.database = { status: 'warn', detail: 'Could not verify database (API unreachable).' };
    }
    setResults({ ...out });

    // 4. Same-origin routing
    try {
      const u = new URL(API);
      const sameHost = u.hostname === window.location.hostname;
      out.origin = sameHost
        ? { status: 'ok', detail: `Calls going to ${u.hostname} (same origin).` }
        : { status: 'warn', detail: `Calls going to ${u.hostname} instead of ${window.location.hostname}. Auto-rewrite patch may be active.` };
    } catch {
      out.origin = { status: 'warn', detail: 'Origin check skipped.' };
    }
    setResults({ ...out });

    setChecking(false);
  };

  useEffect(() => { runChecks(); }, []);

  const allOk = results && Object.values(results).every((x) => x.status === 'ok');
  const anyBad = results && Object.values(results).some((x) => x.status === 'bad');

  const headline = !results
    ? 'Running diagnostics…'
    : allOk
      ? 'All systems are operational.'
      : anyBad
        ? 'We detected an issue.'
        : 'Minor degradation detected.';
  const headlineColor = allOk ? GOOD : anyBad ? BAD : WARN;

  return (
    <div className="min-h-screen bg-[#0c0a09] text-white" data-testid="health-page">
      <div className="container mx-auto px-4 py-10 max-w-2xl">
        <Link to="/" className="inline-flex items-center gap-2 text-[#9CA3AF] hover:text-white text-sm mb-8">
          <ArrowLeft size={14} /> Back to EntrepreNatives
        </Link>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-4" style={{ background: `${headlineColor}15`, border: `1px solid ${headlineColor}40`, color: headlineColor }}>
              <span className="w-2 h-2 rounded-full" style={{ background: headlineColor }} />
              {checking ? 'CHECKING' : (allOk ? 'ALL GOOD' : anyBad ? 'PROBLEM DETECTED' : 'DEGRADED')}
            </div>
            <h1 className="font-syne text-3xl sm:text-4xl font-bold" data-testid="health-headline" style={{ color: headlineColor }}>
              {headline}
            </h1>
            <p className="text-[#9CA3AF] text-sm mt-2">
              Real-time diagnostics from your device. If something fails, share this page's output with support.
            </p>
          </div>

          <div className="space-y-3">
            {results && (
              <>
                <Row icon={Wifi} label="Internet" status={results.internet.status} detail={results.internet.detail} />
                <Row icon={Globe} label="DNS & Site" status={results.dns.status} detail={results.dns.detail} />
                <Row icon={Zap} label="API Backend" status={results.backend.status} detail={results.backend.detail} />
                <Row icon={Database} label="Database" status={results.database.status} detail={results.database.detail} />
                <Row icon={CheckCircle2} label="Same-Origin Routing" status={results.origin.status} detail={results.origin.detail} />
              </>
            )}
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button
              onClick={runChecks}
              disabled={checking}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] disabled:opacity-50"
              data-testid="health-refresh"
            >
              <RefreshCw size={14} className={checking ? 'animate-spin' : ''} /> Re-run checks
            </button>
            {startedAt && (
              <span className="text-[10px] text-[#64748B]">
                Last run at {startedAt.toLocaleTimeString()}
              </span>
            )}
          </div>

          {anyBad && (
            <div className="mt-8 p-5 rounded-2xl border border-red-500/30 bg-red-500/5">
              <p className="text-[#FCA5A5] text-sm font-semibold mb-2">What you can try</p>
              <ul className="text-[#C4B5FD] text-xs space-y-1.5 list-disc list-inside">
                <li>Disable any ad-blocker for entreprenatives.com and retry</li>
                <li>If you're on a corporate network, try a mobile hotspot</li>
                <li>Clear your browser cache & reload (⌘⇧R / Ctrl‑Shift‑R)</li>
                <li>Email <a className="text-[#A78BFA] underline" href="mailto:support@entreprenatives.com">support@entreprenatives.com</a> with a screenshot of this page</li>
              </ul>
            </div>
          )}

          <div className="mt-10 pt-6 border-t border-white/5 text-center">
            <p className="text-[10px] text-[#64748B]">
              Running from {typeof window !== 'undefined' ? window.location.hostname : '—'} · {navigator?.userAgent?.slice(0, 80)}…
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
